export const LOYALTY_REWARD_TYPES = [
  "BONUS",
  "CASHBACK",
  "COINS",
  "RANKING",
  "INFO",
] as const;

export type LoyaltyRewardType = (typeof LOYALTY_REWARD_TYPES)[number];

export interface LoyaltyRewardTranslation {
  locale: string;
  title: string;
  description: string;
}

export interface LoyaltyLevelReward {
  id?: number;
  type: LoyaltyRewardType;
  position: number;
  targetReference: string | null;
  config: { amount?: string; isAvailable?: boolean } | null;
  isAvailable?: boolean;
  translations?: LoyaltyRewardTranslation[];
}

export interface LoyaltyRewardFieldError {
  field: string;
  code: string;
}

const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const LOCALE_PATTERN = /^[a-z]{2}-[A-Z]{2}$/;

export const sortLoyaltyRewards = (
  rewards: LoyaltyLevelReward[] = [],
): LoyaltyLevelReward[] =>
  [...rewards].sort((left, right) => left.position - right.position);

export const getLoyaltyRewardTypeCounts = (
  rewards: LoyaltyLevelReward[] = [],
): Array<{ type: LoyaltyRewardType; count: number }> => {
  const counts = new Map<LoyaltyRewardType, number>();
  rewards.forEach((reward) =>
    counts.set(reward.type, (counts.get(reward.type) ?? 0) + 1),
  );
  return Array.from(counts, ([type, count]) => ({ type, count }));
};

export const replaceBonusRewards = (
  rewards: LoyaltyLevelReward[],
  selectedReferences: string[],
): LoyaltyLevelReward[] => {
  const normalizedReferences = Array.from(
    new Set(
      selectedReferences
        .map((reference) => String(reference).trim())
        .filter(Boolean),
    ),
  );
  const selected = new Set(normalizedReferences);
  const kept = new Set<string>();
  const nextRewards = rewards.filter((reward) => {
    if (reward.type !== "BONUS") return true;
    const reference = reward.targetReference ?? "";
    if (!selected.has(reference) || kept.has(reference)) return false;
    kept.add(reference);
    return true;
  });

  normalizedReferences.forEach((reference) => {
    if (kept.has(reference)) return;
    nextRewards.push({
      type: "BONUS",
      position: nextRewards.length + 1,
      targetReference: reference,
      config: null,
    });
    kept.add(reference);
  });

  return nextRewards;
};

export const normalizeLoyaltyRewards = (
  rewards: LoyaltyLevelReward[],
): LoyaltyLevelReward[] =>
  rewards.map((reward, index) => ({
    type: reward.type,
    position: index + 1,
    targetReference:
      reward.type === "BONUS" || reward.type === "CASHBACK"
        ? (reward.targetReference ?? null)
        : null,
    config:
      reward.type === "COINS" || reward.type === "RANKING"
        ? { amount: String(reward.config?.amount ?? "") }
        : null,
    ...(reward.type === "INFO"
      ? {
          isAvailable: reward.isAvailable !== false,
          translations: (reward.translations ?? [])
            .map((translation) => ({
              locale: translation.locale,
              title: translation.title.trim(),
              description: translation.description.trim(),
            }))
            .filter(
              (translation) =>
                translation.title.length > 0 ||
                translation.description.length > 0,
            ),
        }
      : {}),
  }));

export const validateLoyaltyRewards = (
  rewards: LoyaltyLevelReward[],
): LoyaltyRewardFieldError[] => {
  const errors: LoyaltyRewardFieldError[] = [];

  rewards.forEach((reward, index) => {
    const root = `rewards[${index}]`;

    if (reward.type === "BONUS" || reward.type === "CASHBACK") {
      if (!POSITIVE_INTEGER_PATTERN.test(reward.targetReference ?? "")) {
        errors.push({
          field: `${root}.targetReference`,
          code: "InvalidTargetReference",
        });
      }
      return;
    }

    if (reward.type === "COINS" || reward.type === "RANKING") {
      if (!POSITIVE_INTEGER_PATTERN.test(reward.config?.amount ?? "")) {
        errors.push({
          field: `${root}.config`,
          code: "InvalidRewardConfig",
        });
      }
      return;
    }

    const translations = (reward.translations ?? []).filter(
      (translation) =>
        translation.title.trim().length > 0 ||
        translation.description.trim().length > 0,
    );

    if (translations.length === 0) {
      errors.push({
        field: `${root}.translations`,
        code: "InvalidTranslations",
      });
      return;
    }

    translations.forEach((translation, translationIndex) => {
      const translationRoot = `${root}.translations[${translationIndex}]`;

      if (!LOCALE_PATTERN.test(translation.locale)) {
        errors.push({
          field: `${translationRoot}.locale`,
          code: "InvalidLocale",
        });
      }
      if (!translation.title.trim()) {
        errors.push({
          field: `${translationRoot}.title`,
          code: "NotEmpty",
        });
      }
      if (!translation.description.trim()) {
        errors.push({
          field: `${translationRoot}.description`,
          code: "NotEmpty",
        });
      }
    });
  });

  return errors;
};

export const getInfoRewardTitle = (
  reward: LoyaltyLevelReward,
  localeOrder: string[],
): string => {
  const translations = reward.translations ?? [];

  for (const locale of [...localeOrder, "en-US"]) {
    const translation = translations.find((item) => item.locale === locale);
    if (translation?.title.trim()) return translation.title;
  }

  return (
    translations.find((translation) => translation.title.trim())?.title ?? ""
  );
};
